import { useEffect, useRef, useState } from "react";
import { useUser } from "@clerk/react";
import {
  useGetWorkspaceMilestones,
  useGetWorkspaceAnalyticsEvents,
  useListWorkspaces,
  type WorkspaceMilestoneEventMilestone,
} from "@workspace/api-client-react";
import { useLocation } from "wouter";
import {
  captureProductEvent,
  capturePostHogPageview,
  LT_POSTHOG_EVENTS,
  preparePostHogUser,
  resetPostHog,
  syncPostHogContext,
} from "@/lib/posthog";

const POSTHOG_MILESTONE_EVENTS: Partial<
  Record<
    WorkspaceMilestoneEventMilestone,
    | typeof LT_POSTHOG_EVENTS.icpCreated
    | typeof LT_POSTHOG_EVENTS.campaignCreated
    | typeof LT_POSTHOG_EVENTS.firstEmailSent
    | typeof LT_POSTHOG_EVENTS.positiveReply
    | typeof LT_POSTHOG_EVENTS.meetingBooked
    | typeof LT_POSTHOG_EVENTS.opportunityCreated
  >
> = {
  icp_created: LT_POSTHOG_EVENTS.icpCreated,
  sequence_created: LT_POSTHOG_EVENTS.campaignCreated,
  first_send_accepted: LT_POSTHOG_EVENTS.firstEmailSent,
  first_reply: LT_POSTHOG_EVENTS.positiveReply,
  first_meeting_booked: LT_POSTHOG_EVENTS.meetingBooked,
  first_deal_created: LT_POSTHOG_EVENTS.opportunityCreated,
};

function SignedOutPostHogLifecycle() {
  useEffect(() => {
    resetPostHog();
  }, []);

  return null;
}

function AuthenticatedPostHogLifecycle({ userId }: { userId: string }) {
  const [location] = useLocation();
  const { data, dataUpdatedAt, isSuccess, refetch } = useListWorkspaces();
  const [verifiedContext, setVerifiedContext] = useState<{
    userId: string;
    workspaceId: number | null;
    dataUpdatedAt: number;
  } | null>(null);

  useEffect(() => {
    preparePostHogUser(userId);
    setVerifiedContext(null);

    let cancelled = false;
    void refetch().then((result) => {
      if (cancelled || !result.data) return;
      setVerifiedContext({
        userId,
        workspaceId: result.data.activeWorkspaceId,
        dataUpdatedAt: result.dataUpdatedAt,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [refetch, userId]);

  useEffect(() => {
    if (
      !verifiedContext ||
      verifiedContext.userId !== userId ||
      !isSuccess ||
      dataUpdatedAt <= verifiedContext.dataUpdatedAt
    ) {
      return;
    }

    setVerifiedContext({
      userId,
      workspaceId: data?.activeWorkspaceId ?? null,
      dataUpdatedAt,
    });
  }, [
    data?.activeWorkspaceId,
    dataUpdatedAt,
    isSuccess,
    userId,
    verifiedContext,
  ]);

  useEffect(() => {
    if (!verifiedContext || verifiedContext.userId !== userId) return;
    syncPostHogContext(userId, verifiedContext.workspaceId);
  }, [userId, verifiedContext]);

  const hasVerifiedWorkspace =
    verifiedContext?.userId === userId && verifiedContext.workspaceId !== null;
  const verifiedWorkspaceId = hasVerifiedWorkspace
    ? verifiedContext.workspaceId
    : null;
  const {
    data: milestoneData,
    isFetching: milestonesAreFetching,
    isSuccess: milestonesLoadedSuccessfully,
  } = useGetWorkspaceMilestones({
    query: {
      enabled: verifiedWorkspaceId !== null,
      refetchOnMount: "always",
      staleTime: 0,
      queryKey: [
        "/api/product/milestones",
        userId,
        verifiedWorkspaceId,
        verifiedContext?.dataUpdatedAt ?? null,
      ],
    },
  });
  const {
    data: projectionData,
    isFetching: projectionsAreFetching,
    isSuccess: projectionsLoadedSuccessfully,
  } = useGetWorkspaceAnalyticsEvents({
    query: {
      enabled: verifiedWorkspaceId !== null,
      refetchOnMount: "always",
      staleTime: 0,
      queryKey: [
        "/api/product/events",
        userId,
        verifiedWorkspaceId,
        verifiedContext?.dataUpdatedAt ?? null,
      ],
    },
  });
  const deliveredMilestoneIds = useRef(new Set<string>());
  const deliveredContext = useRef<string | null>(null);
  const currentDeliveryContext =
    verifiedWorkspaceId === null ? null : `${userId}:${verifiedWorkspaceId}`;

  useEffect(() => {
    deliveredMilestoneIds.current.clear();
    deliveredContext.current = currentDeliveryContext;
  }, [currentDeliveryContext]);

  useEffect(() => {
    if (!hasVerifiedWorkspace) return;
    capturePostHogPageview();
  }, [hasVerifiedWorkspace, location, userId]);

  useEffect(() => {
    if (
      currentDeliveryContext === null ||
      deliveredContext.current !== currentDeliveryContext ||
      milestonesAreFetching ||
      !milestonesLoadedSuccessfully ||
      !milestoneData
    ) {
      return;
    }

    for (const milestone of milestoneData.events) {
      const eventName = POSTHOG_MILESTONE_EVENTS[milestone.milestone];
      if (
        !eventName ||
        deliveredMilestoneIds.current.has(milestone.analyticsEventKey)
      ) {
        continue;
      }

      if (
        captureProductEvent(eventName, {
          eventId: milestone.analyticsEventKey,
        })
      ) {
        deliveredMilestoneIds.current.add(milestone.analyticsEventKey);
      }
    }
  });

  useEffect(() => {
    if (
      currentDeliveryContext === null ||
      deliveredContext.current !== currentDeliveryContext ||
      projectionsAreFetching ||
      !projectionsLoadedSuccessfully ||
      !projectionData
    ) {
      return;
    }
    for (const event of projectionData.events) {
      if (deliveredMilestoneIds.current.has(event.eventUuid)) continue;
      const properties = {
        eventId: event.eventUuid,
        ...(event.actionType ? { actionType: event.actionType } : {}),
        ...(event.actionCategory ? { actionCategory: event.actionCategory } : {}),
        ...(event.executionMode ? { executionMode: event.executionMode } : {}),
        ...(event.blockerReasonCode ? { blockerReasonCode: event.blockerReasonCode } : {}),
        ...(event.scoreBand ? { scoreBand: event.scoreBand } : {}),
        ...(event.sourceCategory ? { sourceCategory: event.sourceCategory } : {}),
      };
      if (captureProductEvent(event.eventType, properties as never)) {
        deliveredMilestoneIds.current.add(event.eventUuid);
      }
    }
  });

  return null;
}

export function PostHogLifecycle() {
  const { isLoaded, isSignedIn, user } = useUser();

  if (!isLoaded) return null;
  if (!isSignedIn || !user?.id) return <SignedOutPostHogLifecycle />;

  return <AuthenticatedPostHogLifecycle userId={user.id} />;
}
